import { create } from 'zustand';
import { createJSONStorage, persist, type StateStorage } from 'zustand/middleware';
import { api } from '~/api';
import {
    FALLBACK_RATES,
    convertPriceCeil,
    formatCurrency,
    detectLocaleCurrency,
    type SupportedCurrency
} from '~/lib/currency';

type CurrencyState = {
    selectedCurrency: SupportedCurrency;
    isCustomSelection: boolean;
    rates: Record<string, number>;
    lastFetchedAt: string | null;
    isLoading: boolean;
    setCurrency: (currency: SupportedCurrency) => void;
    fetchRates: (force?: boolean) => Promise<void>;
    // Convenience helper using active store state
    convert: (amount: number | string, fromCurrency?: string) => number;
    format: (amount: number | string, fromCurrency?: string) => string;
};

type WebStorage = {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
};

const browserStorage: StateStorage = {
    getItem: key => (globalThis as { localStorage?: WebStorage }).localStorage?.getItem(key) ?? null,
    setItem: (key, value) => (globalThis as { localStorage?: WebStorage }).localStorage?.setItem(key, value),
    removeItem: key => (globalThis as { localStorage?: WebStorage }).localStorage?.removeItem(key)
};

const CLIENT_CACHE_TTL_MS = 60 * 60 * 1000; // 1 hour

// When this tab last asked the server. The server's own fetchedAt can be hours old
// (it refreshes every 6h), so measuring freshness from it made every page refetch.
let lastAskedAt = 0;

const sameRates = (a: Record<string, number>, b: Record<string, number>): boolean => {
    const keys = Object.keys(b);
    return keys.length === Object.keys(a).length && keys.every(k => a[k] === b[k]);
};

const useCurrency = create<CurrencyState>()(
    persist(
        (set, get) => ({
            selectedCurrency: detectLocaleCurrency(),
            isCustomSelection: false,
            rates: { ...FALLBACK_RATES },
            lastFetchedAt: null,
            isLoading: false,

            setCurrency: (currency: SupportedCurrency) => {
                set({ selectedCurrency: currency, isCustomSelection: true });
            },

            fetchRates: async (force = false) => {
                const state = get();
                if (state.isLoading) return;

                if (!force && Date.now() - lastAskedAt < CLIENT_CACHE_TTL_MS) return;

                set({ isLoading: true });
                lastAskedAt = Date.now();
                try {
                    const res = await api.currency.rates();
                    if (res?.data?.rates) {
                        const next = { ...FALLBACK_RATES, ...res.data.rates };
                        // Keep the old object when nothing changed so subscribers don't re-render or re-search.
                        set({
                            rates: sameRates(state.rates, next) ? state.rates : next,
                            lastFetchedAt: res.data.fetchedAt || new Date().toISOString()
                        });
                    }
                } catch (err) {
                    console.warn('[useCurrency] Failed to sync rates from backend:', err);
                } finally {
                    set({ isLoading: false });
                }
            },

            convert: (amount: number | string, fromCurrency = 'USD') => {
                const { selectedCurrency, rates } = get();
                return convertPriceCeil(amount, fromCurrency, selectedCurrency, rates);
            },

            format: (amount: number | string, fromCurrency = 'USD') => {
                const { selectedCurrency, rates } = get();
                const converted = convertPriceCeil(amount, fromCurrency, selectedCurrency, rates);
                return formatCurrency(converted, selectedCurrency);
            }
        }),
        {
            name: 'suitescanner-currency',
            storage: createJSONStorage(() => browserStorage),
            partialize: state => ({
                selectedCurrency: state.selectedCurrency,
                isCustomSelection: state.isCustomSelection,
                rates: state.rates,
                lastFetchedAt: state.lastFetchedAt
            }),
            onRehydrateStorage: () => state => {
                if (state && !state.isCustomSelection) {
                    state.selectedCurrency = detectLocaleCurrency();
                }
            }
        }
    )
);

export { useCurrency };
